/**
 * Skeleton — animated shimmer placeholder for loading states.
 * Usage:
 *   <Skeleton height="h-8" width="w-32" />
 *   <Skeleton.Group count={3} height="h-12" />
 */
import type { ReactNode } from 'react';

interface SkeletonProps {
  height?: string;
  width?: string;
  className?: string;
  rounded?: string;
}

function SkeletonBase({
  height = 'h-5',
  width = 'w-full',
  className,
  rounded = 'rounded-md',
}: SkeletonProps) {
  return (
    <div
      aria-hidden
      className={[
        'animate-pulse bg-surface-raised',
        height,
        width,
        rounded,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );
}

function SkeletonGroup({
  count = 3,
  gap = 'gap-2',
  ...props
}: SkeletonProps & { count?: number; gap?: string }) {
  return (
    <div className={['flex flex-col', gap].join(' ')}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonBase key={i} {...props} />
      ))}
    </div>
  );
}

export const Skeleton = Object.assign(SkeletonBase, { Group: SkeletonGroup });

/** Stat tile skeleton — square card placeholder. */
export function StatSkeleton() {
  return (
    <div className="rounded-card border border-border bg-surface p-4 shadow-card">
      <Skeleton height="h-3" width="w-20" className="mb-3" />
      <Skeleton height="h-7" width="w-28" className="mb-2" />
      <Skeleton height="h-3" width="w-16" />
    </div>
  );
}

/** Row skeleton for tables / lists. */
export function RowSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 py-3">
          <Skeleton height="h-4" width="w-24" />
          <Skeleton height="h-4" width="w-16" />
          <Skeleton height="h-4" className="flex-1" />
          <Skeleton height="h-4" width="w-12" />
        </div>
      ))}
    </>
  );
}
